/**
 * How a reaction PLAYS OUT on the quantitative bench — what is used up, how
 * long it would really take, and how long the bench takes to show it.
 *
 * The working (`@padcanvas/shared/stoichiometry`) says how much of each reagent
 * is left once the reaction has run; this turns that into something that can
 * be watched. A marble chip in acid does not vanish when the reaction is
 * "done" — it dissolves from the surface inward, so it SHRINKS, fastest at
 * first and slower as both it and the acid run down, and the fizzing tapers
 * off as it goes. That is the picture this module supplies the numbers for.
 *
 * Time is played REAL where it can be and honestly compressed where it cannot:
 * a reaction that would take under ten seconds plays at its own pace, and one
 * that would take longer finishes in ten and SAYS it was sped up, with the real
 * figure beside it. A bench that quietly ran every reaction in five seconds
 * would be teaching that a nail plates with copper as fast as acid meets
 * alkali, which is exactly the misconception a rates lesson exists to undo.
 */
import type { Stoichiometry } from '@padcanvas/shared';
import { findSubstance, type SolidForm } from './substanceCatalog';
import type { VesselContent } from './labState';
import type { ChemistryLabOutcome, TemperatureBand } from './types';

/**
 * How much of each shelf reagent is still there, as a fraction of what went
 * in: 1 untouched, 0 used up entirely. Keyed by substance id, because that is
 * what the bench draws; a reagent the equation never names is absent, and read
 * as untouched.
 */
export function remainingFractions(working: Stoichiometry | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!working) return out;
  for (const reagent of working.reagents) {
    const fraction = reagent.mol > 0 ? Math.max(0, Math.min(1, reagent.leftoverMol / reagent.mol)) : 1;
    // Two shelf reagents supplying one species are used up in proportion —
    // HCl is HCl, and neither bottle's share reacts first.
    for (const id of reagent.substanceIds) out[id] = fraction;
  }
  return out;
}

/**
 * How big a piece looks when this fraction of its mass is left.
 *
 * The cube root, not the fraction: mass goes with volume and volume with the
 * cube of size, so a chip with a third of its mass left is about 0.69 of its
 * size, not a third of it. Drawn at a third it would look nearly gone while
 * two thirds of the reaction's worth of it was still there.
 */
export function sizeForRemaining(fraction: number): number {
  return fraction <= 0 ? 0 : Math.cbrt(fraction);
}

/**
 * About how long each form of solid takes to react away in 1 M acid at room
 * temperature, in seconds — order-of-magnitude figures of what a school bench
 * shows, not measurements. What they get RIGHT is the ordering and the ratios:
 * the SURFACE AREA effect, which a Class 10 syllabus teaches as the first factor
 * in a rate. A powder has vastly more surface than the same mass as one lump,
 * and is gone in a fraction of the time.
 */
const REAL_SECONDS_BY_FORM: Record<SolidForm, number> = {
  powder: 45,       // a spatula of powdered carbonate fizzes out inside a minute
  crystal: 90,
  granules: 300,    // zinc granules in dilute acid: several minutes
  lump: 480,        // marble chips: the classic "watch it for ten minutes"
  strip: 600,
  nail: 1200,       // an iron nail plating with copper: the rest of the lesson
};

/** Two solutions reacting — a neutralisation, a precipitate forming — happen
 *  as fast as the ions meet: effectively instant. */
const IN_SOLUTION_SECONDS = 0.5;

/** The longest the bench will make anyone watch. Past this it finishes on
 *  time and says it sped up. */
export const PLAYBACK_CAP_SECONDS = 10;

/** Short enough to be instant, long enough to be SEEN happening. */
const PLAYBACK_MIN_SECONDS = 0.6;

/**
 * About how long this reaction would take on a real bench, in seconds.
 *
 * From what the bench knows, each factor in the direction and ratio the
 * chemistry gives it:
 *   - the reacting solid's FORM (surface area) sets the base;
 *   - the attacking solution's CONCENTRATION divides it (rate ∝ [acid]), so
 *     2 M takes half as long as 1 M;
 *   - HEAT speeds it — the jump to "heated" is several doublings;
 *   - and the chemistry's own read of how hard the gas comes off adjusts on
 *     top, since it is the model's view of this particular pair.
 *
 * 0 where there is no reaction to time.
 */
export function realReactionSeconds(
  working: Stoichiometry | undefined,
  outcome: ChemistryLabOutcome | undefined,
  contents: VesselContent[],
  band: TemperatureBand,
): number {
  if (!working) return 0;

  // The slowest reacting solid decides it: the reaction is over when the last
  // of it has gone, not the first.
  let base = IN_SOLUTION_SECONDS;
  let strongest = 0;
  for (const reagent of working.reagents) {
    for (const id of reagent.substanceIds) {
      const substance = findSubstance(id);
      if (!substance) continue;
      if (substance.physicalState === 'solid' && substance.form) {
        base = Math.max(base, REAL_SECONDS_BY_FORM[substance.form]);
      }
      if (substance.physicalState === 'aqueous' || substance.physicalState === 'liquid') {
        const held = contents.find(c => c.substanceId === id);
        const m = held?.concentrationM ?? substance.concentrationM ?? 0;
        // Water is a reagent here only in name; it is not what attacks a
        // solid, and its 55.5 M would make every reaction instant.
        if (id !== 'water') strongest = Math.max(strongest, m);
      }
    }
  }

  // Concentration only speeds what a solution does TO a solid. Two solutions
  // meeting are instant already.
  const concentration = base > IN_SOLUTION_SECONDS && strongest > 0 ? 1 / strongest : 1;
  const heat = band === 'boiling' ? 0.15 : band === 'heated' ? 0.3 : 1;
  const rate = outcome?.gasEvolution?.rate;
  const vigour = rate === 'vigorous' ? 0.7 : rate === 'slow' ? 1.5 : 1;

  return base * concentration * heat * vigour;
}

/** How long the bench takes to show it: the real time, or ten seconds if the
 *  real time is longer. In ms, for the animation. */
export function playbackMs(realSeconds: number): number {
  if (realSeconds <= 0) return 0;
  return Math.round(Math.min(PLAYBACK_CAP_SECONDS, Math.max(PLAYBACK_MIN_SECONDS, realSeconds)) * 1000);
}

/** Whether what the bench is showing is faster than the real thing. */
export function isSpedUp(realSeconds: number): boolean {
  return realSeconds > PLAYBACK_CAP_SECONDS;
}

/**
 * The real time, said the way a person would — "about 40 seconds", "about 8
 * minutes". Rounded hard, because these are order-of-magnitude figures and a
 * precise-looking number would claim a measurement nobody made.
 */
export function formatRealDuration(seconds: number): string {
  if (seconds < 90) return `about ${Math.max(1, Math.round(seconds / 5) * 5)} seconds`;
  const minutes = Math.round(seconds / 60);
  return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * What identifies ONE run of the reaction, so the bench can tell a new one
 * from the same one seen again.
 *
 * The amount of product. A pour that makes more reacts again and plays again;
 * a pH-paper dip, or water poured into a finished reaction, makes nothing new —
 * and replaying the fizzing for it would show a reaction that is not happening.
 */
export function reactionRunKey(working: Stoichiometry | undefined): string {
  if (!working) return '';
  return working.products.map(p => `${p.formula}:${p.mol.toPrecision(9)}`).join('|');
}
